import styles from "../../../styles/components/Modal/Affiliate/EditAffiliate.module.css";
import React, { useEffect, useState, useRef, useContext, useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {InputSelector} from "../../UI/InputSelector";
import {DropdownSelector} from "../../UI/DropdownSelector";
import {MultiSelectDropdown} from "../../UI/MultiSelectDropdown";
import {editAffiliate} from "../../../utils/api/Affiliate/affiliateUpdateApi";
import {getAffiliateFields} from "../../../utils/api/Affiliate/affiliateFields";
import {getAllDesksApi} from "../../../utils/api/Admin/desk/getAllDesksApi";
import {getAdmins} from "../../../utils/api/Admin/getAdminsApi";
import {getSaleStatuses} from "../../../utils/api/Lead/getSaleStatusesApi";
import {LangContext} from "../../../context/LangContext";
import {ThemeContext} from "../../../context/ThemeContext";
import { getMyAccountApi } from "../../../utils/api/Account/getMyAccountApi";

const translations = {
  ru: {
    loadingDesksError: "Не удалось загрузить список десков",
    loadingManagersError: "Не удалось загрузить список менеджеров",
    loadingSaleStatusesError: "Не удалось загрузить список статусов продаж",
    unknownError: "Произошла неизвестная ошибка",
    title: "Редактировать Аффилиата",
    confirm: "Подтвердить"
  },
  en: {
    loadingDesksError: "Failed to load desk list",
    loadingManagersError: "Failed to load manager list",
    loadingSaleStatusesError: "Failed to load sale statuses",
    unknownError: "An unknown error occurred",
    title: "Edit Affiliate",
    confirm: "Confirm"
  },
};

export const EditAffiliate = ({ handleEditAffiliate, data, fetchAffiliates }) => {
  const { langSelected } = useContext(LangContext);
  const { theme } = useContext(ThemeContext);
  const t = translations[langSelected] || translations.ru;

  const affiliateFields = useMemo(() => {
    return getAffiliateFields(langSelected);
  }, [langSelected]);

  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [deskOptions, setDeskOptions] = useState([]);
  const [loadingDesks, setLoadingDesks] = useState(true);
  const [deskError, setDeskError] = useState(null);

  const [managerOptions, setManagerOptions] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [managerError, setManagerError] = useState(null);

  const [saleStatusOptions, setSaleStatusOptions] = useState([]);
  const [loadingSaleStatuses, setLoadingSaleStatuses] = useState(true);
  const [saleStatusError, setSaleStatusError] = useState(null);

  const [isIpAdmin, setIsIpAdmin] = useState(false);

  const modalRef = useRef(null);

  const isLoadingData = loadingDesks || loadingManagers || loadingSaleStatuses;

  const getInitialAdminIds = (affiliateData) => {
    if (Array.isArray(affiliateData?.admin_ids)) {
      return affiliateData.admin_ids
        .filter((adminId) => adminId !== null && adminId !== undefined)
        .map((adminId) => String(adminId));
    }

    if (affiliateData?.admin_id !== null && affiliateData?.admin_id !== undefined) {
      return [String(affiliateData.admin_id)];
    }

    return [];
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      handleEditAffiliate();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleEditAffiliate]);

  useEffect(() => {
    const fetchIpAdminStatus = async () => {
      try {
        const accountData = await getMyAccountApi();
        if (accountData && accountData.ip_admin !== undefined) {
          setIsIpAdmin(!!accountData.ip_admin);
        } else {
          setIsIpAdmin(false);
        }
      } catch (error) {
        console.error("Failed to fetch account data for ip_admin check:", error);
        setIsIpAdmin(false);
      }
    };

    fetchIpAdminStatus();
  }, []);

  useEffect(() => {
    if (data) {
      const initialFormData = {};
      affiliateFields.forEach(({ key }) => {
        const rawValue = data[key];

        let initialValue;

        if (key === 'is_blocked') {
          initialValue = Boolean(rawValue);
        } else if (key === 'admin_ids') {
          initialValue = getInitialAdminIds(data);
        } else if (key === 'desk_id') {
          initialValue = (rawValue !== null && rawValue !== undefined) ? String(rawValue) : "";
        } else {
          initialValue = (rawValue !== null && rawValue !== undefined) ? rawValue : "";
        }

        initialFormData[key] = initialValue;
      });
      initialFormData.password = "";


      if (initialFormData.sale_status === "" || initialFormData.sale_status === undefined) {
        initialFormData.sale_status = "New";
      }
      if (initialFormData.is_blocked === undefined) {
        initialFormData.is_blocked = false;
      }

      setFormData(initialFormData);
    }
  }, [data, affiliateFields]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingDesks(true);
      setLoadingManagers(true);
      setLoadingSaleStatuses(true);
      setDeskError(null);
      setManagerError(null);
      setSaleStatusError(null);

      try {
        const [desksResult, adminsResult, saleStatusesResult] = await Promise.all([
          getAllDesksApi(),
          getAdmins(1, 50),
          getSaleStatuses(),
        ]);

        setDeskOptions(desksResult.data.map(desk => ({ value: String(desk.id), label: desk.name })));
        setLoadingDesks(false);

      setManagerOptions(adminsResult.data.map(admin => ({
          value: String(admin.id),
          label: admin.first_name ? `${admin.first_name} ${admin.last_name || ''}`.trim() : admin.email
        })));
        setLoadingManagers(false);

        setSaleStatusOptions((saleStatusesResult.data || []).map(status => ({
          value: status,
          label: status
        })));
        setLoadingSaleStatuses(false);

      } catch (error) {
        if (error.response?.url.includes('desk')) {
          setDeskError(error.message || t.loadingDesksError);
        } else if (error.response?.url.includes('admin')) {
          setManagerError(error.message || t.loadingManagersError);
        } else if (error.response?.url.includes('sale-statuses')) {
          setSaleStatusError(error.message || t.loadingSaleStatusesError);
        } else {
          setDeskError(t.unknownError);
          setManagerError(t.unknownError);
          setSaleStatusError(t.unknownError);
        }
        setLoadingDesks(false);
        setLoadingManagers(false);
        setLoadingSaleStatuses(false);
      }
    };

    fetchData();
  }, [t.loadingDesksError, t.loadingManagersError, t.loadingSaleStatusesError, t.unknownError]);

  useEffect(() => {
    const isReady = !loadingDesks && !loadingManagers && !loadingSaleStatuses && data;

    if (isReady) {
      setFormData(prev => {
        const newFormData = { ...prev };

        const rawDeskId = data.desk_id;
        if (rawDeskId !== null && rawDeskId !== undefined) {
          newFormData.desk_id = String(rawDeskId);
        } else if (newFormData.desk_id === '') {
          newFormData.desk_id = '';
        }

        newFormData.admin_ids = getInitialAdminIds(data);

        newFormData.sale_status = data.sale_status;

        return newFormData;
      });
    }
  }, [data, loadingDesks, loadingManagers, loadingSaleStatuses]);


  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (touched[key]) {
      setErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  const handleFocus = (key) => {
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const handleSubmit = async () => {
    const dataToSend = {};
    const affiliateId = data.id;

    if (dataToSend.sale_status === "") {
      dataToSend.sale_status = null;
    }

    for (const key of ['email', 'affiliate', 'sale_status']) {
      if (formData[key] !== undefined) dataToSend[key] = formData[key];
    }

    for (const key of ['first_name', 'last_name']) {
      if (formData[key] && formData[key].trim() !== '') {
        dataToSend[key] = formData[key];
      } else if (formData[key] === '') {
        dataToSend[key] = null;
      }
    }

    if (formData.password && formData.password.length > 0) {
      dataToSend.password = formData.password;
    }

    if (formData.desk_id) {
      const deskId = parseInt(formData.desk_id, 10);
      if (!isNaN(deskId)) dataToSend.desk_id = deskId;
    } else if (formData.desk_id === "") {
      dataToSend.desk_id = null;
    }

    if (Array.isArray(formData.admin_ids)) {
      const adminIds = formData.admin_ids
        .map((adminId) => parseInt(adminId, 10))
        .filter((adminId) => !isNaN(adminId));

      dataToSend.admin_ids = adminIds;
      dataToSend.admin_id = adminIds[0] ?? null;
    }

    if (isIpAdmin && formData.is_blocked !== undefined) {
      dataToSend.is_blocked = !!formData.is_blocked;
    }

    try {
      await editAffiliate(affiliateId, dataToSend);
      handleEditAffiliate();
      fetchAffiliates();
    } catch (err) {
      if (err && err.errors) {
        setErrors(err.errors);
      } else if (err && err.message) {
        setErrors({ general: err.message });
      } else {
        setErrors({ general: t.unknownError });
      }
    }
  };

  const modalClass = theme === 'dark' ? `${styles.modal} ${styles.darkModal}` : styles.modal;
  const filtersContainerClass = theme === 'dark' ? `${styles.filtersContainer} ${styles.darkFiltersContainer}` : styles.filtersContainer;
  const modalLabelClass = theme === 'dark' ? `${styles.modalLabel} ${styles.darkModalLabel}` : styles.modalLabel;
  const modalContentClass = theme === 'dark' ? `${styles.modalContent} ${styles.darkModalContent}` : styles.modalContent;
  const inputRowLabelClass = theme === 'dark' ? styles.darkLabel : '';
  const textErrorClass = theme === 'dark' ? styles.darkTextError : '';
  const modalFooterClass = theme === 'dark' ? `${styles.modalFooter} ${styles.darkModalFooter}` : styles.modalFooter;
  const submitButtonClass = theme === 'dark' ? `${styles.submitButton} ${styles.darkSubmitButton}` : styles.submitButton;
  const sectionClass = theme === 'dark' ? `${styles.section} ${styles.darkSection}` : styles.section;

  return (
      <div className={modalClass}>
        <div className={filtersContainerClass} ref={modalRef}>
          <div className={modalLabelClass}>
            <p>{t.title}</p>
            <div onClick={handleEditAffiliate}>
              <CloseIcon className={theme === 'dark' ? styles.darkCloseIcon : ''}/>
            </div>
          </div>

          <div className={modalContentClass}>
            <div className={sectionClass}>
              {affiliateFields.map(({key, label, type}) => {

                if (key === 'is_blocked' && !isIpAdmin) {
                  return null;
                }

                const isDesk = key === "desk_id";
                const isManager = key === "admin_ids";
                const isSaleStatus = key === "sale_status";

                let dropdownContent;
                let currentError;
                let currentOptions;
                let selectedValue;

                if (isDesk) {
                  currentError = deskError;
                  currentOptions = deskOptions;
                  selectedValue = formData.desk_id;
                } else if (isManager) {
                  currentError = managerError;
                  currentOptions = managerOptions;
                  selectedValue = Array.isArray(formData.admin_ids) ? formData.admin_ids : [];
                } else if (isSaleStatus) {
                  currentError = saleStatusError;
                  currentOptions = saleStatusOptions;
                  selectedValue = formData.sale_status;
                }

                if (type === "dropdown") {
                  if (currentError) {
                    dropdownContent = <div className={`${styles.textError} ${textErrorClass}`}>{currentError}</div>;
                  } else {
                    dropdownContent = (
                        <DropdownSelector
                            filterName={key}
                            selectedValue={selectedValue}
                            handleFilterChange={handleChange}
                            onBlur={() => handleBlur(key)}
                            onFocus={() => handleFocus(key)}
                            hasError={!!errors[key]}
                            errorMessage={errors[key]}
                            isTouched={touched[key]}
                            options={currentOptions}
                            isLoading={isLoadingData}
                            theme={theme}
                        />
                    );
                  }
                } else if (type === "multiDropdown") {
                  if (currentError) {
                    dropdownContent = <div className={`${styles.textError} ${textErrorClass}`}>{currentError}</div>;
                  } else {
                    dropdownContent = (
                        <MultiSelectDropdown
                            filterName={key}
                            selectedValue={selectedValue}
                            handleFilterChange={handleChange}
                            onBlur={() => handleBlur(key)}
                            onFocus={() => handleFocus(key)}
                            hasError={!!errors[key]}
                            errorMessage={errors[key]}
                            options={currentOptions}
                            shouldAutoClearOnMissingOptions={false}
                            isSearchable
                            closeOnSelect={false}
                        />
                    );
                  }
                }

                return (
                    <div key={key} className={styles.inputRow}>
                      <label className={inputRowLabelClass}>{label}:</label>
                      {type === "input" ? (
                          <InputSelector
                              filterName={key}
                              selectedValue={formData[key]}
                              handleFilterChange={handleChange}
                              onBlur={() => handleBlur(key)}
                              onFocus={() => handleFocus(key)}
                              hasError={!!errors[key]}
                              errorMessage={errors[key]}
                              isTouched={touched[key]}
                              type={key.includes('password') ? 'password' : 'text'}
                              theme={theme}
                          />
                      ) : type === "dropdown" || type === "multiDropdown" ? (
                          dropdownContent
                      ) : type === "checkbox" ? (
                          <input
                              type="checkbox"
                              checked={!!formData[key]}
                              onChange={(e) => handleChange(key, e.target.checked)}
                              onBlur={() => handleBlur(key)}
                          />
                      ) : null}
                    </div>
                );
              })}
              {errors.general && (
                  <div className={`${styles.textError} ${textErrorClass}`}>{errors.general}</div>
              )}
            </div>
          </div>

          <div className={modalFooterClass}>
            <div className={styles.confirmationWrapper}>
              <button
                  className={submitButtonClass}
                  onClick={handleSubmit}
                  disabled={isLoadingData}
              >
                {t.confirm}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};
